import { useContext, useMemo } from 'react'
import { useAtom } from 'jotai'
import { activeProjectScripts, allScripts, defaultScriptUuid, loadScripts, loadingScripts } from '../../../../components/core/ProjectNavigation/atoms'
import { AuthContext } from '../../../../context/firebase-context'
import useWorkspaceSwitcher from '../../../../hooks/useWorkspaceSwitcher'
import { hasFeatureAccess, hasProFeatureEntitlement } from '../../../../common/workspace-entitlements'

// Returns helpers to gate access to per-script FeatureItems for free users with multiple scripts
export default function useFeatureItemAccess ({ featureName, project } = {}) {
  const user = useContext(AuthContext)
  const {
    activeWorkspace,
    workspaces = [],
    workspaceLoading = false,
    workspaceLoaded
  } = useWorkspaceSwitcher()
  const [scripts] = useAtom(allScripts)
  const [projectScripts] = useAtom(activeProjectScripts)
  const [defaultUuid] = useAtom(defaultScriptUuid)
  // An unloaded list looks the same as "denied" to canAccess, so callers that
  // act automatically wait for this.
  const [scriptsPending] = useAtom(loadingScripts)
  const [, refreshScripts] = useAtom(loadScripts)

  const workspacePending = useMemo(() => {
    return !activeWorkspace &&
      workspaceLoaded === false &&
      (workspaceLoading || workspaces.length === 0)
  }, [activeWorkspace, workspaceLoaded, workspaceLoading, workspaces])

  const hasWorkspaceEntitlement = hasProFeatureEntitlement({ activeWorkspace, project, user })

  const featureEnabled = useMemo(() => {
    return hasFeatureAccess({ activeWorkspace, featureName, project, user })
  }, [activeWorkspace, featureName, project, user])

  const isFree = useMemo(() => {
    if (hasWorkspaceEntitlement) return false
    return (user?.subscriptionPlan || 'free') === 'free'
  }, [user, hasWorkspaceEntitlement])

  const effectiveDefaultUuid = useMemo(() => {
    const selectedScript = scripts?.find(script => script?.data?.default || script?.default)
    const selectedProjectScript = projectScripts?.find(script => script?.data?.default || script?.default)
    return defaultUuid || selectedScript?.uuid || selectedProjectScript?.uuid || scripts?.[0]?.uuid || projectScripts?.[0]?.uuid || null
  }, [defaultUuid, projectScripts, scripts])

  const canAccess = (scriptUuid) => {
    // Pro (or any non-free) users always can access
    if (!isFree) return true
    // Free users with multiple scripts should never unlock every script while default state is loading
    if (!effectiveDefaultUuid) return false
    // Only allow defaultScript on free when multiple scripts exist
    return scriptUuid === effectiveDefaultUuid
  }

  const isBlocked = (scriptUuid) => !canAccess(scriptUuid)

  // canAccess says no both for a script the account may not open and for one
  // created since the list was fetched. This tells those apart.
  const knowsScript = (scriptUuid) => {
    if (!scriptUuid) return true
    const inList = list => Array.isArray(list) && list.some(script => script?.uuid === scriptUuid)
    return inList(scripts) || inList(projectScripts)
  }

  return {
    canAccess,
    isBlocked,
    isFree,
    knowsScript,
    refreshScripts,
    defaultUuid: effectiveDefaultUuid,
    featureEnabled,
    scriptsPending,
    workspacePending,
    activeWorkspace
  }
}
